/**
 * API endpoints for Calendar Events
 */

import { ApiService } from '../apiService'
import { ApiResponse } from '../types/common'
import {
  CalendarEventDto,
  CalendarEventListResponse,
  CalendarEventSearchParams,
  CreateCalendarEventRequest,
  UpdateCalendarEventRequest,
  SetRsvpRequest,
  EventTagsRequest,
  EventAttendeesResponse,
  EventSubscriptionLimitsDto,
} from '../types/calendarEvent'

// Import shared criteria types from adventures (same format for events)
import type {
  CriteriaGroupDto,
  AddCriterionRequest,
  UpdateCriterionRequest,
  CriterionResponse,
  CriteriaGroupCreateRequest,
  CriteriaGroupUpdateRequest,
  CriteriaGroupResponse,
} from './adventures'

// Re-export criteria types for convenience
export type {
  CriteriaGroupDto,
  AddCriterionRequest,
  UpdateCriterionRequest,
  CriterionResponse,
  CriteriaGroupCreateRequest,
  CriteriaGroupUpdateRequest,
  CriteriaGroupResponse,
}

export const calendarEventsApi = {
  // ====================
  // EVENT CRUD
  // ====================

  /**
   * Search events with filters and pagination
   */
  searchEvents: (params?: CalendarEventSearchParams) =>
    ApiService.get<CalendarEventListResponse>('/api/events', params),

  /**
   * Get upcoming public events
   */
  getUpcomingEvents: (params?: { page?: number; pageSize?: number }) =>
    ApiService.get<CalendarEventListResponse>('/api/events/upcoming', params),

  /**
   * Get user's events (created or RSVPed to)
   */
  getMyEvents: (params?: { page?: number; pageSize?: number; includeRsvps?: boolean }) =>
    ApiService.get<CalendarEventListResponse>('/api/events/my-events', params),

  /**
   * Get events linked to an adventure
   */
  getEventsByAdventure: (adventureId: string, params?: { page?: number; pageSize?: number }) =>
    ApiService.get<CalendarEventListResponse>(`/api/events/by-adventure/${adventureId}`, params),

  /**
   * Get single event by ID
   */
  getEvent: (id: string) => ApiService.get<CalendarEventDto>(`/api/events/${id}`),

  /**
   * Get event for editing (includes all editor fields)
   */
  getEventForEdit: (id: string) => ApiService.get<CalendarEventDto>(`/api/events/${id}/edit`),

  /**
   * Create new event
   */
  createEvent: (data: CreateCalendarEventRequest) =>
    ApiService.post<ApiResponse<CalendarEventDto>>('/api/events', data),

  /**
   * Update existing event
   */
  updateEvent: (id: string, data: UpdateCalendarEventRequest) =>
    ApiService.put<ApiResponse<CalendarEventDto>>(`/api/events/${id}`, data),

  /**
   * Delete event
   */
  deleteEvent: (id: string) => ApiService.delete<ApiResponse<void>>(`/api/events/${id}`),

  /**
   * Publish draft event
   */
  publishEvent: (id: string) => ApiService.post<ApiResponse<CalendarEventDto>>(`/api/events/${id}/publish`),

  /**
   * Cancel event
   */
  cancelEvent: (id: string, reason?: string) =>
    ApiService.post<ApiResponse<CalendarEventDto>>(`/api/events/${id}/cancel`, { reason }),

  // ====================
  // RSVP
  // ====================

  /**
   * Set RSVP for an event
   */
  setRsvp: (eventId: string, data: SetRsvpRequest) =>
    ApiService.post<ApiResponse<void>>(`/api/events/${eventId}/rsvp`, data),

  /**
   * Remove RSVP from an event
   */
  removeRsvp: (eventId: string) => ApiService.delete<ApiResponse<void>>(`/api/events/${eventId}/rsvp`),

  /**
   * Get event attendees
   */
  getAttendees: (eventId: string, params?: { page?: number; pageSize?: number; status?: number }) =>
    ApiService.get<EventAttendeesResponse>(`/api/events/${eventId}/attendees`, params),

  // ====================
  // TAGS
  // ====================

  /**
   * Get event tags
   */
  getTags: (eventId: string) => ApiService.get<string[]>(`/api/events/${eventId}/tags`),

  /**
   * Add tags to event
   */
  addTags: (eventId: string, data: EventTagsRequest) =>
    ApiService.post<ApiResponse<void>>(`/api/events/${eventId}/tags`, data),

  /**
   * Remove tags from event
   */
  removeTags: (eventId: string, data: EventTagsRequest) =>
    ApiService.delete<ApiResponse<void>>(`/api/events/${eventId}/tags`, { data }),

  /**
   * Save all event tags (replaces existing)
   */
  saveTags: (eventId: string, tagIds: string[]) =>
    ApiService.put<ApiResponse<void>>(`/api/events/${eventId}/tags`, { tagIds }),

  // ====================
  // SUBSCRIPTION LIMITS
  // ====================

  /**
   * Get user's event creation limits
   */
  getSubscriptionLimits: () =>
    ApiService.get<EventSubscriptionLimitsDto>('/api/events/subscription-limits'),

  // ====================
  // DISPLAY CRITERIA
  // ====================

  /**
   * Get display criteria for an event (returns groups with nested criteria)
   */
  getDisplayCriteria: (eventId: string) =>
    ApiService.get<CriteriaGroupDto[]>(`/api/events/${eventId}/display-criteria`),

  /**
   * Add a display criterion
   */
  addDisplayCriterion: (eventId: string, data: AddCriterionRequest) =>
    ApiService.post<CriterionResponse>(`/api/events/${eventId}/display-criteria/add`, data),

  /**
   * Update a display criterion
   */
  updateDisplayCriterion: (eventId: string, criterionId: string, data: UpdateCriterionRequest) =>
    ApiService.put<CriterionResponse>(`/api/events/${eventId}/display-criteria/${criterionId}`, data),

  /**
   * Delete a display criterion
   */
  deleteDisplayCriterion: (eventId: string, criterionId: string) =>
    ApiService.delete<boolean>(`/api/events/${eventId}/display-criteria/${criterionId}`),

  /**
   * Create a display criteria logic group
   */
  createDisplayCriteriaGroup: (eventId: string, data: CriteriaGroupCreateRequest) =>
    ApiService.post<CriteriaGroupResponse>(`/api/events/${eventId}/display-criteria/groups`, data),

  /**
   * Update a display criteria logic group
   */
  updateDisplayCriteriaGroup: (eventId: string, groupId: string, data: CriteriaGroupUpdateRequest) =>
    ApiService.put<CriteriaGroupResponse>(`/api/events/${eventId}/display-criteria/groups/${groupId}`, data),

  /**
   * Delete a display criteria logic group
   */
  deleteDisplayCriteriaGroup: (eventId: string, groupId: string) =>
    ApiService.delete<boolean>(`/api/events/${eventId}/display-criteria/groups/${groupId}`),

  // ====================
  // JOIN CRITERIA
  // ====================

  /**
   * Get join criteria for an event (returns groups with nested criteria)
   */
  getJoinCriteria: (eventId: string) =>
    ApiService.get<CriteriaGroupDto[]>(`/api/events/${eventId}/join-criteria`),

  /**
   * Add a join criterion
   */
  addJoinCriterion: (eventId: string, data: AddCriterionRequest) =>
    ApiService.post<CriterionResponse>(`/api/events/${eventId}/join-criteria/add`, data),

  /**
   * Update a join criterion
   */
  updateJoinCriterion: (eventId: string, criterionId: string, data: UpdateCriterionRequest) =>
    ApiService.put<CriterionResponse>(`/api/events/${eventId}/join-criteria/${criterionId}`, data),

  /**
   * Delete a join criterion
   */
  deleteJoinCriterion: (eventId: string, criterionId: string) =>
    ApiService.delete<boolean>(`/api/events/${eventId}/join-criteria/${criterionId}`),

  /**
   * Create a join criteria logic group
   */
  createJoinCriteriaGroup: (eventId: string, data: CriteriaGroupCreateRequest) =>
    ApiService.post<CriteriaGroupResponse>(`/api/events/${eventId}/join-criteria/groups`, data),

  /**
   * Update a join criteria logic group
   */
  updateJoinCriteriaGroup: (eventId: string, groupId: string, data: CriteriaGroupUpdateRequest) =>
    ApiService.put<CriteriaGroupResponse>(`/api/events/${eventId}/join-criteria/groups/${groupId}`, data),

  /**
   * Delete a join criteria logic group
   */
  deleteJoinCriteriaGroup: (eventId: string, groupId: string) =>
    ApiService.delete<boolean>(`/api/events/${eventId}/join-criteria/groups/${groupId}`),
}
